import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler, annotationPlugin
);

const MS_DIA = 1000 * 60 * 60 * 24;

function diasEntre(a, b) {
  return Math.round((new Date(b) - new Date(a)) / MS_DIA);
}

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

function formatLabel(fecha) {
  return fecha.toLocaleDateString('es-CL', {
    day: '2-digit', month: 'short', year: '2-digit',
    timeZone: 'America/Santiago',
  });
}

function generarSeries(hojas, kpi) {
  const { minInicio, maxFin, hoy, avanceReal, velocidadActual, fechaEstimada } = kpi;
  if (!minInicio || !maxFin) return null;

  const finMax = fechaEstimada && fechaEstimada > maxFin ? fechaEstimada : maxFin;
  const totalDias = diasEntre(minInicio, finMax);
  if (totalDias <= 0) return null;

  // Generar fechas semanales
  const fechas = [];
  for (let d = 0; d <= totalDias; d += 7) {
    fechas.push(new Date(minInicio.getTime() + d * MS_DIA));
  }
  // Asegurar que el último punto sea finMax
  const ultimo = fechas[fechas.length - 1];
  if (ultimo < finMax) fechas.push(new Date(finMax));

  const sumDur = hojas.reduce((s, a) => s + (a.duracionBase || 1), 0);

  const planificado = [];
  const real = [];
  const proyectado = [];

  for (const fecha of fechas) {
    // ── Planificado ──
    let sumEsp = 0;
    for (const a of hojas) {
      if (!a.fechaInicioBase || !a.fechaFinBase) continue;
      const ini = new Date(a.fechaInicioBase);
      const fin = new Date(a.fechaFinBase);
      const rango = (fin - ini) / MS_DIA || 1;
      const avEsp = clamp(((fecha - ini) / MS_DIA) / rango, 0, 1) * 100;
      sumEsp += avEsp * (a.duracionBase || 1);
    }
    planificado.push(Math.round((sumDur > 0 ? sumEsp / sumDur : 0) * 10) / 10);

    // ── Real (solo hasta hoy) ──
    if (fecha <= hoy) {
      let sumR = 0;
      for (const a of hojas) {
        if (!a.fechaInicioBase || !a.fechaFinBase) continue;
        const ini = new Date(a.fechaInicioBase);
        const fin = new Date(a.fechaFinBase);
        const rango = (fin - ini) / MS_DIA || 1;
        const fraccion = clamp(((fecha - ini) / MS_DIA) / rango, 0, 1);
        sumR += (a.porcentajeAvance || 0) * fraccion * (a.duracionBase || 1);
      }
      real.push(Math.round((sumDur > 0 ? sumR / sumDur : 0) * 10) / 10);
    } else {
      real.push(null);
    }

    // ── Proyectado (desde hoy en adelante) ──
    if (fecha >= hoy && velocidadActual > 0) {
      const v = clamp(avanceReal + velocidadActual * diasEntre(hoy, fecha), 0, 100);
      proyectado.push(Math.round(v * 10) / 10);
    } else {
      proyectado.push(null);
    }
  }

  return { fechas, planificado, real, proyectado };
}

export default function CurvaSPrograma({ hojas, kpi }) {
  const series = useMemo(() => generarSeries(hojas, kpi), [hojas, kpi]);

  if (!series) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
        Sin datos de fechas para generar la curva S.
      </div>
    );
  }

  const labels = series.fechas.map(formatLabel);

  // Índice del punto más cercano a "hoy" para la anotación vertical
  const hoyMs = kpi.hoy.getTime();
  const hoyIdx = series.fechas.reduce((best, f, i) => {
    return Math.abs(f - hoyMs) < Math.abs(series.fechas[best] - hoyMs) ? i : best;
  }, 0);

  const data = {
    labels,
    datasets: [
      {
        label: 'Planificado',
        data: series.planificado,
        borderColor: '#3b82f6',
        borderDash: [6, 3],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.35,
        fill: false,
      },
      {
        label: 'Real',
        data: series.real,
        borderColor: '#f97316',
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.35,
        fill: false,
        spanGaps: false,
      },
      {
        label: 'Proyectado',
        data: series.proyectado,
        borderColor: '#94a3b8',
        borderDash: [4, 4],
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0.1,
        fill: false,
        spanGaps: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { usePointStyle: true, pointStyleWidth: 20, font: { size: 12 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y !== null ? ctx.parsed.y + '%' : '—'}`,
        },
      },
      annotation: {
        annotations: {
          hoyLine: {
            type: 'line',
            xMin: hoyIdx,
            xMax: hoyIdx,
            borderColor: '#f97316',
            borderWidth: 1.5,
            borderDash: [4, 4],
            label: {
              display: true,
              content: 'Hoy',
              position: 'start',
              backgroundColor: '#f97316',
              color: '#fff',
              font: { size: 11, weight: '600' },
              padding: { x: 6, y: 3 },
              yAdjust: -6,
            },
          },
          avanceRealLine: {
            type: 'line',
            yMin: kpi.avanceReal,
            yMax: kpi.avanceReal,
            borderColor: 'rgba(249,115,22,0.25)',
            borderWidth: 1,
            borderDash: [3, 3],
            label: {
              display: true,
              content: `${kpi.avanceReal}% real`,
              position: 'end',
              backgroundColor: 'rgba(249,115,22,0.12)',
              color: '#ea580c',
              font: { size: 10, weight: '600' },
              padding: { x: 5, y: 2 },
            },
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 14,
          font: { size: 11 },
          color: '#94a3b8',
        },
        grid: { color: '#f1f5f9' },
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (v) => `${v}%`,
          font: { size: 11 },
          color: '#94a3b8',
          stepSize: 10,
        },
        grid: { color: '#f1f5f9' },
      },
    },
  };

  return (
    <div style={{ height: 360, width: '100%' }}>
      <Line data={data} options={options} />
    </div>
  );
}
